/**
 * Modal grande com o QR de ativação — TH-53163.
 *
 * Carrega o PNG via fetch (para criar Blob URL — permite download) e
 * exibe centralizado. Endpoint `GET /tvs/:id/activation-qr` exige
 * tenant_admin (TH-53147).
 */

import { useEffect, useState } from "react";

import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/Dialog";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tvId: string;
  tvName?: string;
}

async function fetchQR(tvId: string): Promise<Blob> {
  const res = await fetch(`/api/tvs/${tvId}/activation-qr`, {
    credentials: "include",
  });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  return await res.blob();
}

export function ActivationQRDialog({
  open,
  onOpenChange,
  tvId,
  tvName,
}: Props) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    let revoked: string | null = null;
    fetchQR(tvId)
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        revoked = url;
        setBlobUrl(url);
      })
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
    return () => {
      if (revoked) URL.revokeObjectURL(revoked);
      setBlobUrl(null);
    };
  }, [open, tvId]);

  function downloadPNG() {
    if (!blobUrl) return;
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = `priceshow-tv-${tvName ?? tvId}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogTitle>QR de ativação</DialogTitle>
        <DialogDescription>
          Escaneie no app Priceshow do Android Box para ativar
          {tvName ? ` "${tvName}"` : null}.
        </DialogDescription>

        {/* Fundo branco fixo, independente do tema do admin: o QR precisa de
            alto contraste (escuro sobre branco) pra ser lido pela câmera do
            Android Box. bg-background (dark) deixaria o QR ilegível. aderencia:tema-fixo */}
        <div className="mt-6 flex min-h-[280px] items-center justify-center rounded-md bg-white p-6"> {/* aderencia:tema-fixo */}
          {loading ? (
            <span className="text-sm text-[var(--color-muted-foreground)]">
              gerando QR…
            </span>
          ) : error ? (
            <span className="text-sm text-[var(--color-destructive)]">{error}</span>
          ) : blobUrl ? (
            <img
              src={blobUrl}
              alt="QR code de ativação"
              className="h-64 w-64"
            />
          ) : null}
        </div>

        <div className="mt-4 flex justify-between">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Fechar
          </Button>
          <Button onClick={downloadPNG} disabled={!blobUrl}>
            Baixar PNG
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
